import { Button, Form, Row } from "react-bootstrap";
import { TireSetFormPartModel } from "./carTireSetsFormPartModel";
import { Content } from "../../../../general/modules/commons/content/content";
import { ContentHeader } from "../../../../general/modules/commons/content/contentHeader";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { CarTireInputWrapper } from "../carTireFormPart/carTireInputWrapper";
import { CarTireSetModel } from "../../../domain/models/carTireSetModel";
import { CarTireFormPart } from "../carTireFormPart/carTireFormPart";
import { Link } from "react-router-dom";
import { useAuthorization } from "../../../../../hooks/useAuthorization";
import { RequiredAsterisks } from "../../../../general/modules/commons/Asterisks/requiredAsterisks";
import { Snow, TrashFill, SunFill, CloudSunFill } from "react-bootstrap-icons";
import { deleteTireSet } from "../../../../tireset/infrastructure/slices/tireSetMutateSlice";
import { useAppDispatch } from "../../../../../hooks/useTypedSelector";
import swal from "sweetalert";
import { removeTireset } from "../../../infrastructure/slices/carMutateSlice";
import { RequestProgressBar } from "../../../../request/modules/commons/requestProgressBar/requestProgressBar";
import { RequestProgressBarStepModel } from "../../../../request/modules/commons/requestProgressBar/requestProgressBarModel";
import { EntitySelectField } from "../../../../general/fields/modules/commons/entitySelectField/entitySelectField";
import { positionLabelSchema } from "../../../../storage/position/domain/schemas/positionLabelSchema";
import { CarTireModel } from "../../../domain/models/carTireModel";
import { tirePositions } from "../../../domain/lists/tirePositionList";
import { CarTireSetsFormPartRequest } from "./carTireSetsFormPartRequest";

export const CarTireSetsFormPart = ({
  form,
  car,
  licensePlate,
  isFreestock = false,
}: TireSetFormPartModel) => {
  const { t } = useTranslation("common");
  const { hasRole } = useAuthorization();
  const largeScreenSize = 1240;
  const dispatch = useAppDispatch();

  // Generate tires for a new tireset without `id` so the backend assigns DB ids.
  const tires = tirePositions.map(
    (p) =>
      ({
        car_position: p,
      }) as CarTireModel,
  );

  const onDeleteTireset = (id: number) => {
    void swal({
      title: t("crud.tiresets.delete.confirm"),
      buttons: {
        cancel: {
          visible: true,
          className: "btn btn-light btn-sm",
        },
        confirm: {
          visible: true,
          className: "btn btn-primary btn-sm",
        },
      },
    }).then((willDelete) => {
      if (willDelete) {
        void dispatch(deleteTireSet(id)).then((result) => {
          if (deleteTireSet.fulfilled.match(result)) {
            dispatch(removeTireset(id));
            form.setFieldValue(
              "tireSets",
              form.state.values.tireSets?.filter((set) => set.id !== id),
            );
          }
        });
      }
    });
  };

  const stateFlowMargin =
    screen.width < largeScreenSize ? "mt-4 mb-3" : "mt-2 mb-5";

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
      {/* @ts-ignore*/}
      <form.Field name="tireSets" mode="array">
        {(field) => {
          return (
            <Row style={{ rowGap: "20px" }}>
              {field.state.value.map((value: CarTireSetModel, i) => {
                return (
                  <Content
                    md={6}
                    key={i}
                    className="tire-set"
                    id={"tireset-" + value.id}
                  >
                    <ContentHeader
                      title={
                        <span className={"d-flex gap-2"}>
                          {t("Tire set")}
                          {value.type === "winter" && (
                            <Snow fill="var(--bs-info)" />
                          )}
                          {value.type === "summer" && (
                            <SunFill fill="var(--bs-yellow)" />
                          )}
                          {value.type === "all_season" && (
                            <CloudSunFill className={"all-season-icon"} />
                          )}
                        </span>
                      }
                    >
                      {screen.width >= largeScreenSize &&
                        value.id &&
                        value.car_id && (
                          <CarTireSetsFormPartRequest
                            tireSet={value}
                            car={car}
                            isFreestock={isFreestock}
                            licensePlate={licensePlate}
                          />
                        )}
                      {screen.width >= largeScreenSize &&
                        value?.sticker?.pdfUrl && (
                          <Link to={value?.sticker?.pdfUrl} target={"_blank"}>
                            <Button size="sm">{t("sticker.open")}</Button>
                          </Link>
                        )}
                      {screen.width >= largeScreenSize &&
                        value &&
                        value.inHotel && (
                          <span
                            className={
                              "badge bg-in-hotel text-success badge-lg"
                            }
                          >
                            {t("In hotel")}
                          </span>
                        )}
                      {screen.width >= largeScreenSize &&
                        value &&
                        !value.inHotel && (
                          <span
                            className={"badge bg-danger text-danger badge-lg"}
                          >
                            {t("Not in hotel")}
                          </span>
                        )}
                    </ContentHeader>

                    {screen.width < largeScreenSize && value && (
                      <>
                        {value.id && value.car_id && (
                          <Link
                            className="mb-1"
                            to={`/tire-sets/${value.id}/request`}
                            state={{
                              licencePlate: licensePlate,
                              tireType: value.type,
                            }}
                          >
                            {value.storedLatestRequest ? (
                              <Button
                                className="mb-1"
                                size="sm"
                                variant={"success"}
                              >
                                {t(
                                  `request.edit_button.${value.storedLatestRequest.type}`,
                                )}
                              </Button>
                            ) : (
                              <>
                                {!hasRole("dbh-employee") &&
                                  !hasRole("dbh-driver") &&
                                  !hasRole("dbh-order-picker") && (
                                    <Button size="sm">
                                      {t("request.add_button")}
                                    </Button>
                                  )}
                              </>
                            )}
                          </Link>
                        )}

                        {value?.sticker?.pdfUrl && (
                          <Link
                            className="mb-1"
                            to={value?.sticker?.pdfUrl}
                            target={"_blank"}
                          >
                            <Button size="sm">
                              {t("tireset.qr.preview.button")}
                            </Button>
                          </Link>
                        )}

                        {value.inHotel && (
                          <span
                            className={
                              "badge bg-in-hotel text-success badge-lg mb-1"
                            }
                          >
                            {t("In hotel")}
                          </span>
                        )}
                      </>
                    )}

                    {value.activeRequests &&
                      value.activeRequests.length > 0 &&
                      value.activeRequests.map((request) => {
                        const date = new Date(
                          request.pickupAndDeliveryDate as string,
                        );
                        const formattedDate = new Intl.DateTimeFormat("en-GB")
                          .format(date)
                          .replace(/\//g, "-");

                        return (
                          <div className={"mb-3"} key={request.id as number}>
                            <div
                              className={"px-5 " + stateFlowMargin + " pb-2"}
                            >
                              <RequestProgressBar
                                stateFlow={
                                  request.stateFlow as RequestProgressBarStepModel[]
                                }
                                size={"small"}
                              />
                            </div>

                            <div className={"small"}>
                              <div>
                                <strong>{t("Dealer")}:</strong>
                                {` ${request.relation && typeof request.relation === "object" && "name" in request.relation ? request.relation.name : ""}`}
                              </div>
                              <div>
                                <strong>{t("b_o_date")}:</strong>{" "}
                                {formattedDate}
                              </div>
                            </div>

                            <hr />
                          </div>
                        );
                      })}

                    {(!value.activeRequests ||
                      value.activeRequests.length === 0) &&
                      value.storedLatestRequest && (
                        <div
                          className={"mb-3"}
                          key={value.storedLatestRequest.id as number}
                        >
                          <div className={"px-5 " + stateFlowMargin + " pb-2"}>
                            <RequestProgressBar
                              stateFlow={
                                value.storedLatestRequest
                                  .stateFlow as RequestProgressBarStepModel[]
                              }
                              size={"small"}
                            />
                          </div>

                          <div className={"small"}>
                            <div>
                              <strong>{t("Dealer")}:</strong>{" "}
                              {
                                (
                                  value.storedLatestRequest.relation as {
                                    id: number;
                                    name?: string;
                                  }
                                )?.name
                              }
                            </div>
                            <div>
                              <strong>{t("b_o_date")}:</strong>{" "}
                              {(() => {
                                const date = new Date(
                                  value.storedLatestRequest
                                    .pickupAndDeliveryDate as string,
                                );
                                return new Intl.DateTimeFormat("en-GB")
                                  .format(date)
                                  .replace(/\//g, "-");
                              })()}
                            </div>
                          </div>

                          <hr />
                        </div>
                      )}

                    {(hasRole("super-admin") ||
                      hasRole("dbh-manager") ||
                      hasRole("dbh-employee")) && (
                      <form.Field
                        key={`tireSets[${i}].sleepingSet`}
                        name={`tireSets[${i}].sleepingSet`}
                      >
                        {(subField) => {
                          return (
                            <Form.Group className="mb-3">
                              <Form.Check
                                id={subField.name}
                                name={subField.name}
                                checked={subField.state.value}
                                onBlur={subField.handleBlur}
                                onChange={(e) => {
                                  subField.setValue(e.target.checked);
                                }}
                                isInvalid={field.state.meta.errors.length > 0}
                                type="checkbox"
                                label={t("Sleeping set")}
                              />
                            </Form.Group>
                          );
                        }}
                      </form.Field>
                    )}

                    {(hasRole("super-admin") ||
                      hasRole("dbh-manager") ||
                      hasRole("dbh-employee")) && (
                      <form.Field
                        name={`tireSets[${i}].position_id`}
                        key="position"
                        validators={{
                          onChange: z.string().optional().nullable(),
                        }}
                      >
                        {(field) => {
                          return (
                            <Form.Group className="mb-3">
                              <Form.Label>{t("Position")}</Form.Label>
                              <EntitySelectField
                                entity="Position"
                                prefix="positions"
                                labelPrefix={"label"}
                                id={field.name}
                                key={field.name}
                                name={field.name}
                                clearable={true}
                                isInvalid={field.state.meta.errors.length > 0}
                                value={field.state.value}
                                onChange={(option) => {
                                  const value = option
                                    ? (option as { value: number }).value
                                    : null;
                                  field.handleChange(value);
                                }}
                                schema={positionLabelSchema}
                                limit={100}
                              />
                              {field.state.meta.errors ? (
                                <Form.Control.Feedback type="invalid">
                                  {t(field.state.meta.errors.join(", "))}
                                </Form.Control.Feedback>
                              ) : null}
                            </Form.Group>
                          );
                        }}
                      </form.Field>
                    )}

                    <form.Field
                      key={`tireSets[${i}].type`}
                      name={`tireSets[${i}].type`}
                      validators={{
                        onChange: z.string({
                          required_error: t("Required"),
                        }),
                      }}
                    >
                      {(subField) => {
                        return (
                          <Form.Group className="mb-3">
                            <Form.Label>
                              Type <RequiredAsterisks />
                            </Form.Label>
                            <Form.Select
                              id={subField.name}
                              name={subField.name}
                              value={subField.state.value}
                              onBlur={subField.handleBlur}
                              onChange={(e) => {
                                if (
                                  e.target.value === "winter" ||
                                  e.target.value === "summer" ||
                                  e.target.value === "all_season"
                                )
                                  subField.setValue(e.target.value);
                              }}
                              isInvalid={subField.state.meta.errors.length > 0}
                            >
                              <option value=""></option>
                              <option value="winter">{t("Winter")}</option>
                              <option value="summer">{t("Summer")}</option>
                              <option value="all_season">
                                {t("all_season")}
                              </option>
                            </Form.Select>
                            {subField.state.meta.errors ? (
                              <Form.Control.Feedback type="invalid">
                                {subField.state.meta.errors.join(", ")}
                              </Form.Control.Feedback>
                            ) : null}
                          </Form.Group>
                        );
                      }}
                    </form.Field>

                    <form.Field
                      name={`tireSets[${i}].tire_rim`}
                      validators={{
                        onChange: z.string({
                          required_error: t("Required"),
                        }),
                      }}
                    >
                      {(field) => {
                        return (
                          <Form.Group className="mb-3">
                            <Form.Label>
                              {t("Tyre rim type")} <RequiredAsterisks />{" "}
                            </Form.Label>
                            <Form.Select
                              id={field.name}
                              name={field.name}
                              value={field.state.value}
                              onBlur={field.handleBlur}
                              onChange={(e) => {
                                field.setValue(e.target.value);
                              }}
                              isInvalid={field.state.meta.errors.length > 0}
                            >
                              <option value=""></option>
                              <option value="loose_tire">
                                {t("Loose tire")}
                              </option>
                              <option value="tire_steel_rim">
                                {t("Tire + steel rim")}
                              </option>
                              <option value="tire_alloy_rim">
                                {t("Tire + alloy rim")}
                              </option>
                            </Form.Select>
                            {field.state.meta.errors ? (
                              <Form.Control.Feedback type="invalid">
                                {field.state.meta.errors.join(", ")}
                              </Form.Control.Feedback>
                            ) : null}
                          </Form.Group>
                        );
                      }}
                    </form.Field>

                    <CarTireInputWrapper>
                      <CarTireFormPart
                        index={i}
                        form={form}
                        isFreestock={isFreestock}
                      />
                    </CarTireInputWrapper>

                    {!hasRole("dealer-employee") && (
                      <form.Field name={`tireSets[${i}].comment`}>
                        {(field) => {
                          return (
                            <Form.Group className="mb-3">
                              <Form.Label>{t("Comment")}</Form.Label>
                              <Form.Control
                                as="textarea"
                                id={field.name}
                                key={field.name}
                                name={field.name}
                                value={field.state.value || ""}
                                placeholder={
                                  !field.state.value ? t("Comment") : undefined
                                }
                                onChange={(e) =>
                                  field.handleChange(e.target.value)
                                }
                              />
                              {field.state.meta.errors ? (
                                <Form.Control.Feedback type="invalid">
                                  {field.state.meta.errors.join(", ")}
                                </Form.Control.Feedback>
                              ) : null}
                            </Form.Group>
                          );
                        }}
                      </form.Field>
                    )}
                    {!hasRole("dealer-employee") && (
                      <div className={"d-flex flex-row-reverse"}>
                        <Button
                          className={"my-3 btn-light"}
                          onClick={() => {
                            if (value && value.id) {
                              onDeleteTireset(value.id);
                            } else {
                              void field.removeValue(i);
                            }
                          }}
                        >
                          <TrashFill size={16} color="black" />
                        </Button>
                      </div>
                    )}
                  </Content>
                );
              })}
              <Content md={6}>
                <Button
                  variant="success"
                  className="w-100"
                  // eslint-disable-next-line @typescript-eslint/no-misused-promises
                  onClick={() => {
                    field.pushValue({
                      tires: tires,
                    } as CarTireSetModel);
                  }}
                >
                  {t("Add new tire set")}
                </Button>
              </Content>
            </Row>
          );
        }}
      </form.Field>
    </form>
  );
};
